import { useEffect } from 'react';
import { CheckCircle, XCircle, CreditCard } from 'lucide-react';
import { RepairActionCard } from './RepairActionCard';
import { AccountingNote } from './AccountingNote';
import { useReconciliation } from '../useReconciliation';
import { reconciliationApi } from '../reconciliation-api';
import type { RepairActionResult } from '../../invoice-repair/types';

export function StepVerification() {
  const { diagnostic, dateFrom, dateTo, steps, updateStepStatus, addRepairLog, repairLog, isExecuting, setIsExecuting } = useReconciliation();

  const stats = diagnostic?.stats;

  // Derive verification status from other steps — but avoid infinite loop
  // by only updating when the computed status actually changes.
  const syncStatus = steps.find(s => s.id === 'sync')?.status;
  const missingStatus = steps.find(s => s.id === 'missing')?.status;
  const numberingStatus = steps.find(s => s.id === 'numbering')?.status;
  const verificationStatus = steps.find(s => s.id === 'verification')?.status;
  const verificationSummary = steps.find(s => s.id === 'verification')?.summary;

  useEffect(() => {
    if (!stats) {
      if (verificationStatus !== 'idle') updateStepStatus('verification', 'idle');
      return;
    }
    const allOk = syncStatus === 'done' && missingStatus === 'done' && numberingStatus === 'done';
    const newStatus = allOk ? 'done' : 'warning';
    const newSummary = allOk ? 'Gotowe do JPK' : 'Nie wszystkie kroki zakończone';
    if (verificationStatus !== newStatus || verificationSummary !== newSummary) {
      updateStepStatus('verification', newStatus, newSummary);
    }
  }, [stats, syncStatus, missingStatus, numberingStatus, verificationStatus, verificationSummary]);

  if (!diagnostic) {
    return <div className="text-center py-12 text-text-secondary text-sm">Uruchom diagnostykę w kroku 1.</div>;
  }

  const logAction = (action: string, description: string, result: RepairActionResult) => {
    addRepairLog({
      action,
      description,
      changes: (result.invoices || result.results || []).slice(0, 20).map((inv: any) => ({
        invoiceId: inv.id || '',
        field: 'payment_date',
        oldValue: 'brak',
        newValue: inv.paid_date || 'opłacono',
      })),
      result: result.failCount && result.failCount > 0 ? 'partial' : 'success',
    });
  };

  const checklist = [
    {
      label: 'Synchronizacja',
      description: 'EcomFlow = Fakturownia',
      ok: steps.find(s => s.id === 'sync')?.status === 'done',
      details: stats ? `${stats.ecomflowInvoiceCount} / ${stats.fakturowniaVatCount}` : '',
    },
    {
      label: 'Brakujące faktury',
      description: '0 brakujących',
      ok: steps.find(s => s.id === 'missing')?.status === 'done',
      details: stats ? `${stats.missingInvoices} brakujących` : '',
    },
    {
      label: 'Numeracja',
      description: 'Brak luk i faktur z błędnym miesiącem',
      ok: steps.find(s => s.id === 'numbering')?.status === 'done',
      details: stats ? `${stats.numberingGaps} luk, ${stats.wrongMonth} z błędnym miesiącem` : '',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Checklist */}
      <div className="space-y-2">
        {checklist.map((item, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 p-3 rounded-lg border ${
              item.ok
                ? 'bg-success/5 border-success/20'
                : 'bg-danger/5 border-danger/20'
            }`}
          >
            {item.ok
              ? <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
              : <XCircle className="w-4 h-4 text-danger flex-shrink-0" />
            }
            <div className="flex-1 min-w-0">
              <div className="text-sm text-text-primary">{item.label}</div>
              <div className="text-xs text-text-muted">{item.description}</div>
            </div>
            <span className="text-xs text-text-secondary">{item.details}</span>
          </div>
        ))}
      </div>

      {/* Unpaid invoices */}
      {stats && stats.unpaidInvoices > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-surface-raised/30 border border-border-subtle text-xs text-text-secondary">
            <CreditCard className="w-3.5 h-3.5 text-warning" />
            <span><strong>{stats.unpaidInvoices}</strong> nieopłaconych faktur — możesz oznaczyć jako opłacone</span>
          </div>
          <RepairActionCard
            title="Nieopłacone faktury"
            description={`Oznacz ${stats.unpaidInvoices} faktur jako opłacone`}
            count={stats.unpaidInvoices}
            severity="info"
            disabled={isExecuting}
            onPreview={() => reconciliationApi.fixPaymentDates(true, dateFrom, dateTo)}
            onExecute={async () => {
              setIsExecuting(true);
              try {
                const res = await reconciliationApi.fixPaymentDates(false, dateFrom, dateTo);
                logAction('fix-payment-dates', `Oznaczono ${res.count} faktur jako opłacone`, res);
                return res;
              } finally { setIsExecuting(false); }
            }}
          />
        </div>
      )}

      {/* All green */}
      {checklist.every(c => c.ok) && (
        <div className="p-4 rounded-xl bg-success/5 border border-success/20 text-center">
          <CheckCircle className="w-8 h-8 text-success mx-auto mb-2" />
          <h3 className="text-sm font-medium text-success mb-1">Gotowe do JPK</h3>
          <p className="text-xs text-text-secondary">Wszystkie kroki weryfikacji zakończone pomyślnie</p>
        </div>
      )}

      {/* Accounting note */}
      <AccountingNote repairLog={repairLog} dateFrom={dateFrom} dateTo={dateTo} stats={stats || null} />
    </div>
  );
}
